import { AxiosError } from 'axios';
import { inspectionClient } from '@/config/api';
import { apiErrorHandler } from '@/helpers/apiErrorHandler';

export interface VideoSession {
  id: string;
  inspectionItemId: string;
  type: number;
  typeDescription: string;
  status: number;
  statusDescription: string;
  title: string;
  description?: string;
  scheduledStartTime: string;
  scheduledEndTime: string;
  actualStartTime?: string;
  actualEndTime?: string;
  uniqueLink?: string;
  guestLink?: string;
  meetingId?: string;
  maxParticipants?: number;
  requiresConfirmation: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt?: string;
  participants: VideoSessionParticipant[];
  schedules: VideoSessionSchedule[];
}

export interface VideoSessionParticipant {
  id: string;
  videoSessionId: string;
  userId: string;
  userName: string;
  userEmail: string;
  role: number;
  roleDescription: string;
  status: number;
  statusDescription: string;
  confirmedAt?: string;
  declinedAt?: string;
  declineReason?: string;
  joinedAt?: string;
  leftAt?: string;
  createdAt: string;
}

export interface VideoSessionSchedule {
  id: string;
  videoSessionId: string;
  inspectionItemId: string;
  scheduleDate: string;
  timeSlot: number;
  timeSlotDescription: string;
  notes?: string;
  isConfirmed: boolean;
  confirmedAt?: string;
  confirmedBy?: string;
  scheduledByUserId: string;
  scheduledByUserName: string;
  createdAt: string;
  updatedAt?: string;
}

export interface ScheduleVideoSessionRequest {
  inspectionItemId: string;
  scheduleDate: string;
  timeSlot?: number;
  startTime?: string;
  notes?: string;
  type?: number;
  title?: string;
  description?: string;
  maxParticipants?: number;
  requiresConfirmation?: boolean;
  participantEmails?: string[];
}

export interface CreateVideoSessionRequest {
  inspectionItemId: string;
  type: number;
  title: string;
  description?: string;
  scheduledStartTime: string;
  scheduledEndTime: string;
  maxParticipants?: number;
  requiresConfirmation?: boolean;
  participantUserIds?: string[];
}

export interface AddParticipantRequest {
  userId: string;
  role: number;
  notes?: string;
}

export interface ConfirmVideoSessionRequest {
  confirm: boolean;
  declineReason?: string;
  notes?: string;
}

class VideoSessionService {
  private readonly basePath = '/api/video-sessions';
  private readonly scheduleBasePath = '/api/video-session-schedules';

  async createVideoSession(request: CreateVideoSessionRequest): Promise<VideoSession> {
    const response = await inspectionClient.post<VideoSession>(this.basePath, request);
    return response.data;
  }

  async getVideoSessionById(id: string): Promise<VideoSession> {
    const response = await inspectionClient.get<VideoSession>(`${this.basePath}/${id}`);
    return response.data;
  }

  async getVideoSessionsByInspectionItemId(inspectionItemId: string): Promise<VideoSession[]> {
    try {
      const response = await inspectionClient.get<VideoSession[]>(
        `${this.basePath}/inspection-item/${inspectionItemId}`
      );
      return response.data || [];
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response?.status === 404) {
        return [];
      }
      throw error;
    }
  }

  async startVideoSession(sessionId: string): Promise<VideoSession> {
    const response = await inspectionClient.post<VideoSession>(
      `${this.basePath}/${sessionId}/start`
    );
    return response.data;
  }

  async endVideoSession(sessionId: string): Promise<VideoSession> {
    const response = await inspectionClient.post<VideoSession>(
      `${this.basePath}/${sessionId}/end`
    );
    return response.data;
  }

  async skipVideoSession(sessionId: string): Promise<VideoSession> {
    const response = await inspectionClient.post<VideoSession>(
      `${this.basePath}/${sessionId}/skip`
    );
    return response.data;
  }

  async cancelVideoSession(sessionId: string, reason?: string): Promise<VideoSession> {
    const response = await inspectionClient.post<VideoSession>(
      `${this.basePath}/${sessionId}/cancel`,
      { reason }
    );
    return response.data;
  }

  async generateUniqueLink(sessionId: string): Promise<string> {
    const response = await inspectionClient.post<{ uniqueLink: string }>(
      `${this.basePath}/${sessionId}/generate-link`
    );
    return response.data.uniqueLink;
  }

  async addParticipant(sessionId: string, request: AddParticipantRequest): Promise<VideoSession> {
    const response = await inspectionClient.post<VideoSession>(
      `${this.basePath}/${sessionId}/participants`,
      request
    );
    return response.data;
  }

  async confirmParticipation(
    sessionId: string,
    participantId: string,
    request: ConfirmVideoSessionRequest
  ): Promise<VideoSession> {
    const response = await inspectionClient.post<VideoSession>(
      `${this.basePath}/${sessionId}/participants/${participantId}/confirm`,
      request
    );
    return response.data;
  }

  async getMyVideoSessions(): Promise<VideoSession[]> {
    const response = await inspectionClient.get<VideoSession[]>(`${this.basePath}/my-sessions`);
    return response.data;
  }

  async validateAccess(sessionId: string): Promise<{ hasAccess: boolean; message: string }> {
    const response = await inspectionClient.post(`${this.basePath}/${sessionId}/validate-access`);
    return response.data;
  }

  async scheduleVideoSession(request: ScheduleVideoSessionRequest): Promise<VideoSessionSchedule> {
    const response = await inspectionClient.post<VideoSessionSchedule>(
      this.scheduleBasePath,
      request
    );
    return response.data;
  }

  async getScheduleById(id: string): Promise<VideoSessionSchedule> {
    const response = await inspectionClient.get<VideoSessionSchedule>(
      `${this.scheduleBasePath}/${id}`
    );
    return response.data;
  }

  async getSchedulesByInspectionItemId(inspectionItemId: string): Promise<VideoSessionSchedule[]> {
    try {
      const response = await inspectionClient.get<VideoSessionSchedule[]>(
        `${this.scheduleBasePath}/inspection-item/${inspectionItemId}`
      );
      return response.data || [];
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response?.status === 404) {
        return [];
      }
      throw error;
    }
  }

  async getSchedulesByVideoSessionId(videoSessionId: string): Promise<VideoSessionSchedule[]> {
    const response = await inspectionClient.get<VideoSessionSchedule[]>(
      `${this.scheduleBasePath}/video-session/${videoSessionId}`
    );
    return response.data;
  }

  async confirmSchedule(scheduleId: string): Promise<VideoSessionSchedule> {
    const response = await inspectionClient.post<VideoSessionSchedule>(
      `${this.scheduleBasePath}/${scheduleId}/confirm`
    );
    return response.data;
  }

  async checkConflicts(
    inspectionItemId: string,
    scheduleDate: string,
    timeSlot: number
  ): Promise<boolean> {
    const response = await inspectionClient.get<{ hasConflict: boolean }>(
      `${this.scheduleBasePath}/check-conflicts`,
      {
        params: {
          inspectionItemId,
          scheduleDate,
          timeSlot,
        },
      }
    );
    return response.data.hasConflict;
  }

  async searchUserByEmail(email: string): Promise<UserDto> {
    try {
      const response = await inspectionClient.get<UserDto>(
        `/api/video-session-schedules/users/search?email=${encodeURIComponent(email)}`
      );
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response?.status === 404) {
        throw new Error('Usuário não encontrado');
      }
      apiErrorHandler(error, 'Erro ao buscar usuário');
    }
  }

  async searchUsers(searchTerm?: string): Promise<UserDto[]> {
    try {
      const params = searchTerm ? { search: searchTerm } : {};
      const response = await inspectionClient.get<UserDto[]>(
        `/api/video-session-schedules/users/list`,
        { params }
      );
      return response.data || [];
    } catch {
      return [];
    }
  }

  async resendInviteEmail(videoSessionId: string, participantId: string): Promise<void> {
    await inspectionClient.post(
      `${this.scheduleBasePath}/video-session/${videoSessionId}/resend-invite/${participantId}`
    );
  }
}

export interface UserDto {
  id: string;
  userId?: string;
  name: string;
  email: string;
  cpf?: string;
  providerId?: string;
  allowsDirectAssignment?: boolean;
  allowsDemandReceiving?: boolean;
}

export interface VideoSessionCapture {
  id: string;
  videoSessionId: string;
  inspectionItemId: string;
  fileName: string;
  fileSizeBytes: number;
  fileType: string;
  fileUrl: string;
  bucketPath?: string;
  bucketName?: string;
  description?: string;
  checklistItemId?: string;
  capturedAt: string;
  capturedBy: string;
  uploadStatus: number;
  uploadStatusDescription: string;
  errorMessage?: string;
  entityId?: string;
  documentType?: string;
  documentId?: string;
  createdAt: string;
}

export interface UploadVideoCaptureRequest {
  description?: string;
  checklistItemId?: string;
}

export class VideoSessionCaptureService {
  private readonly basePath = '/api/video-sessions';

  async uploadCapture(
    videoSessionId: string,
    file: File,
    request?: UploadVideoCaptureRequest
  ): Promise<VideoSessionCapture> {
    const formData = new FormData();
    formData.append('file', file);
    if (request?.description) {
      formData.append('description', request.description);
    }
    if (request?.checklistItemId) {
      formData.append('checklistItemId', request.checklistItemId);
    }

    const response = await inspectionClient.post<VideoSessionCapture>(
      `${this.basePath}/${videoSessionId}/captures`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }

  async getCapturesByVideoSessionId(videoSessionId: string): Promise<VideoSessionCapture[]> {
    const response = await inspectionClient.get<VideoSessionCapture[]>(
      `${this.basePath}/${videoSessionId}/captures`
    );
    return response.data;
  }

  async getCaptureById(videoSessionId: string, captureId: string): Promise<VideoSessionCapture> {
    const response = await inspectionClient.get<VideoSessionCapture>(
      `${this.basePath}/${videoSessionId}/captures/${captureId}`
    );
    return response.data;
  }

  async updateCapture(
    videoSessionId: string,
    captureId: string,
    options: { description?: string; file?: File }
  ): Promise<VideoSessionCapture> {
    const formData = new FormData();
    if (options.description !== undefined) {
      formData.append('description', options.description);
    }
    if (options.file) {
      formData.append('file', options.file);
    }

    const response = await inspectionClient.put<VideoSessionCapture>(
      `${this.basePath}/${videoSessionId}/captures/${captureId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }

  async deleteCapture(videoSessionId: string, captureId: string): Promise<void> {
    await inspectionClient.delete(`${this.basePath}/${videoSessionId}/captures/${captureId}`);
  }
}

export interface VideoSessionChatMessage {
  id: string;
  videoSessionId: string;
  senderId: string;
  senderName: string;
  senderEmail: string;
  message: string;
  messageType: number;
  messageTypeDescription: string;
  attachmentUrl?: string;
  sentAt: string;
  createdAt: string;
}

export interface SendChatMessageRequest {
  message: string;
  messageType?: number;
  attachmentUrl?: string;
  senderName?: string;
}

export interface VideoSessionHistory {
  videoSessionId: string;
  sessionTitle: string;
  status: number;
  actualStartTime?: string;
  actualEndTime?: string;
  messages: VideoSessionChatMessage[];
  captures: VideoSessionCapture[];
  totalMessages: number;
  totalCaptures: number;
}

export class VideoSessionChatService {
  private readonly basePath = '/api/video-sessions';

  async sendMessage(
    videoSessionId: string,
    request: SendChatMessageRequest
  ): Promise<VideoSessionChatMessage> {
    const response = await inspectionClient.post<VideoSessionChatMessage>(
      `${this.basePath}/${videoSessionId}/chat/messages`,
      request
    );
    return response.data;
  }

  async getMessages(videoSessionId: string): Promise<VideoSessionChatMessage[]> {
    const response = await inspectionClient.get<VideoSessionChatMessage[]>(
      `${this.basePath}/${videoSessionId}/chat/messages`
    );
    return response.data;
  }

  async getHistory(videoSessionId: string): Promise<VideoSessionHistory> {
    const response = await inspectionClient.get<VideoSessionHistory>(
      `${this.basePath}/${videoSessionId}/chat/history`
    );
    return response.data;
  }

  async getMessageById(videoSessionId: string, messageId: string): Promise<VideoSessionChatMessage> {
    const response = await inspectionClient.get<VideoSessionChatMessage>(
      `${this.basePath}/${videoSessionId}/chat/messages/${messageId}`
    );
    return response.data;
  }

  async deleteMessage(videoSessionId: string, messageId: string): Promise<void> {
    await inspectionClient.delete(`${this.basePath}/${videoSessionId}/chat/messages/${messageId}`);
  }
}

export const videoSessionService = new VideoSessionService();
export const videoSessionCaptureService = new VideoSessionCaptureService();
export const videoSessionChatService = new VideoSessionChatService();

